'use client';

import { VillagesData } from '@/actions/villages';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationLinkInactive,
  PaginationNext,
  PaginationPrevious,
} from '@/components/Pagination';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

type Props = { villagesData: VillagesData; currentPage: number };

export function PaginationFooter({ villagesData, currentPage }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getPageUrl = useCallback(
    (page: number) => {
      // set all current params except "page"
      const params: Record<string, string> = {};
      for (const [key, value] of searchParams.entries()) {
        if (key !== 'page') {
          params[key] = value;
        }
      }

      if (page !== 1) {
        params.page = page.toString();
      }

      const searchParamsString = new URLSearchParams(params).toString();

      let href = searchParamsString
        ? `${pathname}?${searchParamsString}`
        : pathname;

      return href;
    },
    [pathname, searchParams],
  );

  const pageItems: ('ellipsis' | number)[] = useMemo(() => {
    return Array(villagesData.pagination.pages)
      .fill(0)
      .map((_, i) => i + 1);
  }, [villagesData.pagination.pages]);

  // const pageItems = useMemo(() => {
  //   const totalPages = villagesData.pagination.pages;

  //   if (totalPages <= 0) return [];

  //   if (totalPages <= 6) {
  //     return Array(totalPages)
  //       .fill(0)
  //       .map((_, i) => i + 1);
  //   }

  //   const items: ('ellipsis' | number)[] = [];
  //   const addedPages = new Set();
  //   const boundaryCount = 3;
  //   const siblingCount = 3;

  //   for (let i = 1; i <= boundaryCount; i++) {
  //     if (!addedPages.has(i)) {
  //       items.push(i);
  //       addedPages.add(i);
  //     }
  //   }

  //   const startMiddle = Math.max(currentPage - siblingCount, boundaryCount + 1);
  //   if (startMiddle > boundaryCount + 1) {
  //     items.push('ellipsis');
  //   }

  //   const endMiddle = Math.min(
  //     currentPage + siblingCount,
  //     totalPages - boundaryCount,
  //   );
  //   for (let i = startMiddle; i <= endMiddle; i++) {
  //     if (!addedPages.has(i)) {
  //       items.push(i);
  //       addedPages.add(i);
  //     }
  //   }

  //   const endBoundaryStart = totalPages - boundaryCount + 1;
  //   if (endMiddle < endBoundaryStart - 1) {
  //     items.push('ellipsis');
  //   }

  //   for (let i = endBoundaryStart; i <= totalPages; i++) {
  //     if (!addedPages.has(i)) {
  //       items.push(i);
  //       addedPages.add(i);
  //     }
  //   }

  //   return items;
  // }, [currentPage, villagesData.pagination.pages]);

  if (pageItems.length === 0) return null;

  return (
    <div className="mt-4 mb-8">
      <Pagination>
        <PaginationContent className="flex flex-row flex-wrap justify-center gap-x-[10px] gap-y-[3px]">
          {currentPage !== 1 ? (
            <PaginationItem>
              <PaginationPrevious
                href={getPageUrl(currentPage - 1)}
                isActive={currentPage === 1}
              />
            </PaginationItem>
          ) : (
            <PaginationItem>
              <PaginationLinkInactive className="cursor-not-allowed text-gray-500 hover:text-gray-500">
                <FiChevronLeft className="w-5 h-5 mx-auto" />
              </PaginationLinkInactive>
            </PaginationItem>
          )}

          <div className="flex flex-wrap tablet:gap-[5px] justify-center">
            {pageItems.map((item, index) => {
              if (item === 'ellipsis') {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={item}>
                  <PaginationLink
                    href={getPageUrl(item)}
                    isActive={item === currentPage}>
                    {item}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
          </div>

          {currentPage !== Math.floor(villagesData.pagination.pages) ? (
            <PaginationItem>
              <PaginationNext
                href={getPageUrl(currentPage + 1)}
                isActive={
                  currentPage === Math.floor(villagesData.pagination.pages)
                }
              />
            </PaginationItem>
          ) : (
            <PaginationItem>
              <PaginationLinkInactive className="cursor-not-allowed text-gray-500 hover:text-gray-500">
                <FiChevronRight className="w-5 h-5 mx-auto" />
              </PaginationLinkInactive>
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
}
